import type { GearSlot } from '../model/types.ts';

/** Launch items stop at tier 3. Tier 4 is the next silhouette, ready for painted art. */
export type ShapeTier = 1 | 2 | 3 | 4;

export type ShapeRole = 'body' | 'wrap' | 'stud' | 'plate' | 'pauldron';

export type EnhanceRole = 'strap' | 'spike' | 'filigree' | 'flourish';

export interface ShapePoint {
  x: number;
  y: number;
}

export interface ShapePart {
  role: ShapeRole;
  points: ShapePoint[];
}

export interface EnhanceAddon {
  at: 3 | 6 | 9 | 10;
  role: EnhanceRole;
  /** Socket on the base piece. The outline is drawn around this point. */
  socket: ShapePoint;
  points: ShapePoint[];
}

export interface SlotShape {
  anchor: ShapePoint;
  tiers: Record<ShapeTier, ShapePart[]>;
  enhance: EnhanceAddon[];
}

const p = (x: number, y: number): ShapePoint => ({ x, y });
const poly = (...pairs: number[]): ShapePoint[] => {
  const points: ShapePoint[] = [];
  for (let index = 0; index < pairs.length; index += 2) points.push(p(pairs[index]!, pairs[index + 1]!));
  return points;
};

function addons(scale: number): EnhanceAddon[] {
  const s = scale;
  return [
    {
      at: 3,
      role: 'strap',
      socket: p(0, 8 * s),
      points: poly(-34 * s, -5 * s, 36 * s, -7 * s, 40 * s, 7 * s, -30 * s, 9 * s),
    },
    {
      at: 6,
      role: 'spike',
      socket: p(0, -18 * s),
      points: poly(-48 * s, 6 * s, -34 * s, -28 * s, -18 * s, 6 * s, 0, -34 * s, 20 * s, 6 * s, 36 * s, -28 * s, 52 * s, 8 * s),
    },
    {
      at: 9,
      role: 'filigree',
      socket: p(0, 28 * s),
      points: poly(-40 * s, 0, -28 * s, 12 * s, -8 * s, 2 * s, 6 * s, 14 * s, 22 * s, 2 * s, 40 * s, 12 * s, 44 * s, -2 * s),
    },
    {
      at: 10,
      role: 'flourish',
      socket: p(0, -36 * s),
      points: poly(-6 * s, 4 * s, -7 * s, -22 * s, -16 * s, -16 * s, 0, -40 * s, 16 * s, -16 * s, 7 * s, -22 * s, 6 * s, 4 * s),
    },
  ];
}

/**
 * Placeholder silhouettes. A later painted PNG can replace each part in place.
 * Points are figure space relative to the slot anchor. Y grows downward.
 */
export const GEAR_SHAPES: Record<GearSlot, SlotShape> = {
  weapon: {
    anchor: p(34, -118),
    tiers: {
      1: [
        { role: 'body', points: poly(0, 10, 8, 16, 18, -6, 28, -28, 22, -18, 14, 4, 6, -8) },
        { role: 'wrap', points: poly(-2, 6, 10, 2, 12, 12, 2, 16) },
      ],
      2: [
        { role: 'body', points: poly(2, 14, 10, 8, 16, -8, 22, -36, 18, -48, 10, -40, 6, -16, 0, 4) },
        { role: 'stud', points: poly(12, -10, 16, -14, 20, -10, 16, -6) },
        { role: 'stud', points: poly(8, -28, 12, -32, 16, -28, 12, -24) },
      ],
      3: [
        { role: 'plate', points: poly(0, 12, 6, -8, 4, -46, 16, -20) },
        { role: 'plate', points: poly(10, 8, 28, -6, 22, -58, 8, -24) },
        { role: 'stud', points: poly(14, -16, 17, -19, 20, -16, 17, -13) },
      ],
      4: [
        { role: 'body', points: poly(4, 16, 22, 16, 22, -62, 4, -62) },
        { role: 'pauldron', points: poly(-2, 8, 28, 8, 28, -8, -2, -8) },
      ],
    },
    enhance: addons(0.7),
  },
  offhand: {
    anchor: p(-46, -132),
    tiers: {
      1: [
        { role: 'body', points: poly(-8, -18, 6, -22, 16, -4, 10, 22, -4, 16, -14, 4) },
        { role: 'wrap', points: poly(-12, -2, 4, 2, 2, 12, -14, 8) },
      ],
      2: [
        { role: 'body', points: poly(-16, -8, -8, -28, 10, -30, 20, -12, 16, 16, 0, 28, -14, 18) },
        { role: 'stud', points: poly(-2, -4, 2, -8, 6, -4, 2, 0) },
        { role: 'stud', points: poly(-4, 10, 0, 6, 4, 10, 0, 14) },
      ],
      3: [
        { role: 'plate', points: poly(-20, -24, -2, -30, 2, 8, -22, 4) },
        { role: 'plate', points: poly(0, -8, 22, -16, 18, 26, -4, 18) },
        { role: 'stud', points: poly(6, 2, 9, -1, 12, 2, 9, 5) },
      ],
      4: [
        { role: 'body', points: poly(-18, -32, 18, -32, 18, 28, -18, 28) },
        { role: 'pauldron', points: poly(-28, -18, -16, -18, -16, 10, -28, 10) },
      ],
    },
    enhance: addons(0.85),
  },
  helm: {
    anchor: p(8, -204),
    tiers: {
      1: [
        { role: 'body', points: poly(-10, -6, 2, -16, 14, -8, 18, 6, 8, 14, -6, 10, -16, 2) },
        { role: 'wrap', points: poly(-14, 4, 16, 0, 14, 8, -12, 10) },
      ],
      2: [
        { role: 'body', points: poly(-16, 2, -12, -14, 0, -20, 16, -14, 20, 0, 12, 12, -8, 14) },
        { role: 'stud', points: poly(-4, -6, 0, -10, 4, -6, 0, -2) },
        { role: 'stud', points: poly(6, 2, 10, -2, 14, 2, 10, 6) },
      ],
      3: [
        { role: 'plate', points: poly(-18, -4, -2, -18, 2, 8, -16, 10) },
        { role: 'plate', points: poly(0, -16, 20, -6, 16, 12, 2, 4) },
        { role: 'stud', points: poly(4, -2, 7, -5, 10, -2, 7, 1) },
      ],
      4: [
        { role: 'body', points: poly(-16, -18, 16, -18, 16, 14, -16, 14) },
        { role: 'pauldron', points: poly(-28, -8, -14, -8, -14, 8, -28, 8) },
        { role: 'pauldron', points: poly(14, -8, 28, -8, 28, 8, 14, 8) },
      ],
    },
    enhance: addons(0.72),
  },
  chest: {
    anchor: p(0, -148),
    tiers: {
      1: [
        { role: 'body', points: poly(-26, -6, -18, 22, -6, 14, 4, 28, 18, 12, 24, -4, 10, -18, -8, -16, -22, -20) },
        { role: 'wrap', points: poly(-28, 4, -6, -2, 8, 8, 20, 2, 16, 12, -4, 10, -24, 14) },
      ],
      2: [
        {
          role: 'body',
          points: poly(-30, -4, -22, -22, -4, -26, 16, -20, 28, -6, 26, 16, 10, 28, -12, 26, -28, 12),
        },
        { role: 'stud', points: poly(-16, -8, -12, -12, -8, -8, -12, -4) },
        { role: 'stud', points: poly(8, -6, 12, -10, 16, -6, 12, -2) },
        { role: 'stud', points: poly(-2, 8, 2, 4, 6, 8, 2, 12) },
      ],
      3: [
        { role: 'plate', points: poly(-40, -18, -6, -28, -2, 24, -36, 30) },
        { role: 'plate', points: poly(-4, -8, 34, -22, 42, 8, 18, 34, 2, 16) },
        { role: 'stud', points: poly(-16, 2, -13, -1, -10, 2, -13, 5) },
        { role: 'stud', points: poly(16, -4, 19, -7, 22, -4, 19, -1) },
      ],
      4: [
        { role: 'body', points: poly(-28, -20, 28, -20, 28, 32, -28, 32) },
        { role: 'pauldron', points: poly(-58, -28, -26, -28, -26, 6, -58, 6) },
        { role: 'pauldron', points: poly(26, -28, 58, -28, 58, 6, 26, 6) },
      ],
    },
    enhance: addons(1),
  },
  gloves: {
    anchor: p(30, -112),
    tiers: {
      1: [
        { role: 'body', points: poly(-8, -4, 2, -12, 12, -2, 8, 12, -2, 14, -10, 4) },
        { role: 'wrap', points: poly(-10, 2, 8, -2, 10, 6, -8, 8) },
      ],
      2: [
        { role: 'body', points: poly(-10, 0, -6, -12, 6, -14, 14, -4, 12, 10, 2, 14, -8, 10) },
        { role: 'stud', points: poly(0, -2, 3, -5, 6, -2, 3, 1) },
      ],
      3: [
        { role: 'plate', points: poly(-12, -6, -2, -14, 0, 6, -10, 8) },
        { role: 'plate', points: poly(0, -4, 14, -8, 12, 12, 2, 8) },
        { role: 'stud', points: poly(4, 0, 6, -2, 8, 0, 6, 2) },
      ],
      4: [
        { role: 'body', points: poly(-8, -12, 12, -12, 12, 12, -8, 12) },
        { role: 'pauldron', points: poly(-16, -6, -6, -6, -6, 6, -16, 6) },
      ],
    },
    enhance: addons(0.55),
  },
  boots: {
    anchor: p(0, -28),
    tiers: {
      1: [
        { role: 'body', points: poly(-16, -8, -8, 16, 2, 10, 6, 20, 16, 6, 10, -14, -4, -18) },
        { role: 'wrap', points: poly(-14, 0, 8, -4, 12, 4, -10, 8) },
      ],
      2: [
        { role: 'body', points: poly(-18, -6, -10, -20, 4, -22, 16, -10, 18, 8, 8, 20, -8, 18, -16, 8) },
        { role: 'stud', points: poly(-4, -4, 0, -8, 4, -4, 0, 0) },
        { role: 'stud', points: poly(2, 8, 6, 4, 10, 8, 6, 12) },
      ],
      3: [
        { role: 'plate', points: poly(-20, -16, -4, -22, -2, 10, -18, 14) },
        { role: 'plate', points: poly(-2, -6, 16, -14, 20, 12, 2, 18) },
        { role: 'stud', points: poly(4, 0, 7, -3, 10, 0, 7, 3) },
      ],
      4: [
        { role: 'body', points: poly(-16, -24, 16, -24, 16, 16, -16, 16) },
        { role: 'pauldron', points: poly(-24, -8, -14, -8, -14, 10, -24, 10) },
        { role: 'pauldron', points: poly(14, -8, 24, -8, 24, 10, 14, 10) },
      ],
    },
    enhance: addons(0.8),
  },
};
